<template>
  <v-container fluid class="draw-editor">
    <div class="page-stack">
      <div
        v-for="(page, index) in templatePages"
        :key="page.id"
        ref="pageLayers"
        class="page-layer"
      >
        <img
          class="page-image"
          :src="`${baseUrl}${page.path}`"
          :alt="`Booking invoice page ${index + 1}`"
          @load="imageLoaded(index)"
        />
        <canvas
          ref="pageCanvases"
          class="page-canvas"
          @mousedown="setActivePage(index)"
          @touchstart.passive="setActivePage(index)"
          @pointerdown="setActivePage(index)"
        ></canvas>
      </div>
    </div>

    <img
      class="appendix-page"
      :src="`${baseUrl}/pdfs/images/13.jpg`"
      alt="Booking invoice appendix page"
    />

    <v-btn @click="promptForEmailAndSave" color="primary">Save</v-btn>

    <v-dialog v-model="emailDialog" persistent>
      <v-card>
        <v-card-title>
          Send to an email address
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="userEmail"
            label="Email"
            required
          ></v-text-field>
          <v-text-field
            v-model="userPhone"
            label="Phone"
            required
          ></v-text-field>
          <v-text-field
            v-model="userBusinessName"
            label="Business Name"
            required
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="saveSignatureWithEmail" color="primary">OK</v-btn>
          <v-btn @click="cancelSignatureSave" color="error">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" timeout="5000" bottom :color="snackbarColor">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import SignaturePad from 'signature_pad'
import axios from 'axios'

const TEMPLATE_PAGES = [
  { id: 'first', path: '/pdfs/images/1_new.jpg' },
  { id: 'tenth', path: '/pdfs/images/11.jpg' },
  { id: 'eleventh', path: '/pdfs/images/2_new.jpg' },
  { id: 'second', path: '/pdfs/images/3.jpg' },
  { id: 'third', path: '/pdfs/images/4.jpg' },
  { id: 'fourth', path: '/pdfs/images/5_new.jpg' },
  { id: 'fifth', path: '/pdfs/images/6.jpg' },
  { id: 'sixth', path: '/pdfs/images/7_new.jpg' },
  { id: 'seventh', path: '/pdfs/images/9.jpg' },
  { id: 'eight', path: '/pdfs/images/8.jpg' },
  { id: 'ninth', path: '/pdfs/images/10_new.jpg' }
]

export default {
  props: ['draw'],

  data() {
    return {
      baseUrl: 'https://www.bluebusinessguide.com.au',
      templatePages: TEMPLATE_PAGES,
      pagePads: [],
      pageReady: TEMPLATE_PAGES.map(() => false),
      activePageIndex: 0,
      activeTool: 'pen',
      pendingDummy: false,
      pendingDummyPayload: null,
      pendingDummyCapture: false,
      emailDialog: false,
      userEmail: '',
      userPhone: '',
      userBusinessName: '',
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      appliedDrawSource: '',
      resizeTimer: null
    }
  },

  mounted() {
    this.emitInterface()
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
    this.pagePads.forEach(signaturePad => {
      if (signaturePad && typeof signaturePad.off === 'function') {
        signaturePad.off()
      }
    })
  },

  watch: {
    draw: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.appliedDrawSource = ''
          return
        }
        this.maybeApplyExistingDrawing(value)
      }
    }
  },

  methods: {
    imageLoaded(index) {
      this.$nextTick(() => {
        this.setupPagePad(index)
        this.maybeApplyExistingDrawing(this.draw)
        this.flushPendingDummy()
      })
    },

    getPageLayers() {
      const layers = this.$refs.pageLayers
      return Array.isArray(layers) ? layers : layers ? [layers] : []
    },

    getPageCanvases() {
      const canvases = this.$refs.pageCanvases
      return Array.isArray(canvases) ? canvases : canvases ? [canvases] : []
    },

    getPageLayer(index) {
      return this.getPageLayers()[index] || null
    },

    getPageCanvas(index) {
      return this.getPageCanvases()[index] || null
    },

    setupPagePad(index) {
      const layer = this.getPageLayer(index)
      const canvas = this.getPageCanvas(index)
      if (!layer || !canvas) {
        return
      }

      const cssWidth = Math.round(layer.clientWidth)
      const cssHeight = Math.round(layer.clientHeight)
      if (!cssWidth || !cssHeight) {
        return
      }

      canvas.width = cssWidth
      canvas.height = cssHeight
      canvas.style.width = `${cssWidth}px`
      canvas.style.height = `${cssHeight}px`

      const context = canvas.getContext('2d')
      if (!context) {
        return
      }

      context.setTransform(1, 0, 0, 1, 0, 0)
      context.clearRect(0, 0, canvas.width, canvas.height)

      let signaturePad = this.pagePads[index]
      if (!signaturePad) {
        signaturePad = new SignaturePad(canvas, {
          backgroundColor: 'rgba(255, 255, 255, 0)',
          penColor: 'rgb(0, 0, 0)',
          minWidth: 0.7,
          maxWidth: 1.8,
          velocityFilterWeight: 0.6,
          throttle: 4,
          minDistance: 1
        })
        signaturePad.addEventListener('beginStroke', () => {
          this.setActivePage(index)
          this.configurePadForTool(index)
        })
        this.$set(this.pagePads, index, signaturePad)
      } else {
        signaturePad.clear()
      }

      this.configurePadForTool(index)
      this.$set(this.pageReady, index, true)
    },

    configurePadForTool(index) {
      const signaturePad = this.pagePads[index]
      const canvas = this.getPageCanvas(index)
      if (!signaturePad || !canvas) {
        return
      }

      const context = canvas.getContext('2d')
      if (!context) {
        return
      }

      if (this.activeTool === 'eraser') {
        signaturePad.minWidth = 10
        signaturePad.maxWidth = 22
        signaturePad.penColor = 'rgba(0, 0, 0, 1)'
        context.globalCompositeOperation = 'destination-out'
        canvas.classList.add('page-canvas--eraser')
      } else {
        signaturePad.minWidth = 0.7
        signaturePad.maxWidth = 1.8
        signaturePad.penColor = 'rgb(0, 0, 0)'
        context.globalCompositeOperation = 'source-over'
        canvas.classList.remove('page-canvas--eraser')
      }
    },

    allPagesReady() {
      return this.pageReady.length === this.templatePages.length
        ? this.pageReady.every(Boolean)
        : false
    },

    maybeApplyExistingDrawing(drawSource) {
      if (!drawSource || !this.allPagesReady()) {
        return
      }
      if (this.appliedDrawSource === drawSource) {
        return
      }
      this.loadCompositeImage(drawSource)
    },

    loadCompositeImage(drawSource) {
      const canvases = this.getPageCanvases()
      if (!canvases.length) {
        return
      }

      const image = new Image()
      image.onload = () => {
        const totalHeight = canvases.reduce(
          (sum, canvas) => sum + canvas.height,
          0
        )
        if (!totalHeight || !image.naturalHeight) {
          return
        }

        this.pagePads.forEach(signaturePad => {
          if (signaturePad) {
            signaturePad.clear()
          }
        })

        const scaleY = image.naturalHeight / totalHeight
        let sourceOffsetY = 0

        canvases.forEach((canvas, index) => {
          const context = canvas.getContext('2d')
          if (!context) {
            return
          }

          const sourceHeight =
            index === canvases.length - 1
              ? image.naturalHeight - sourceOffsetY
              : Math.round(canvas.height * scaleY)

          context.clearRect(0, 0, canvas.width, canvas.height)
          context.drawImage(
            image,
            0,
            sourceOffsetY,
            image.naturalWidth,
            sourceHeight,
            0,
            0,
            canvas.width,
            canvas.height
          )

          sourceOffsetY += sourceHeight
        })

        this.appliedDrawSource = drawSource
      }
      image.src = drawSource
    },

    setActivePage(index) {
      this.activePageIndex = index
    },

    setTool(tool) {
      this.activeTool = tool === 'eraser' ? 'eraser' : 'pen'
      this.templatePages.forEach((page, index) => {
        this.configurePadForTool(index)
      })
    },

    promptForEmailAndSave() {
      this.emailDialog = true
    },

    async saveSignatureWithEmail() {
      this.emailDialog = false

      if (!this.userEmail || !this.userPhone || !this.userBusinessName) {
        return alert('Email, Phone, and Business Name are required.')
      }

      const data = this.buildCompositeImage()
      if (!data) {
        return alert('Please provide a drafting first.')
      }

      try {
        const response = await axios.post('/api/admin/images', {
          email: this.userEmail,
          phone: this.userPhone,
          business_name: this.userBusinessName,
          img: data
        })

        const savedImage =
          response.data && response.data.image && response.data.image.img
            ? response.data.image.img
            : data

        this.$store.dispatch({ type: 'setImg', value: savedImage })
        this.snackbarMessage = 'Draft saved successfully!'
        this.snackbarColor = 'success'
        this.snackbar = true
      } catch (error) {
        console.error('Error saving signature:', error)
        this.snackbarMessage = 'Error saving signature. Please try again.'
        this.snackbarColor = 'error'
        this.snackbar = true
      }
    },

    cancelSignatureSave() {
      this.emailDialog = false
    },

    buildCompositeImage() {
      const canvases = this.getPageCanvases()
      if (!canvases.length || !canvases.some(this.canvasHasInk)) {
        return ''
      }

      const width = Math.max(...canvases.map(canvas => canvas.width))
      const height = canvases.reduce((sum, canvas) => sum + canvas.height, 0)

      if (!width || !height) {
        return ''
      }

      const combinedCanvas = document.createElement('canvas')
      combinedCanvas.width = width
      combinedCanvas.height = height

      const context = combinedCanvas.getContext('2d')
      if (!context) {
        return ''
      }

      let offsetY = 0
      canvases.forEach(canvas => {
        context.drawImage(canvas, 0, offsetY, canvas.width, canvas.height)
        offsetY += canvas.height
      })

      return combinedCanvas.toDataURL('image/png')
    },

    canvasHasInk(canvas) {
      if (!canvas) {
        return false
      }

      const context = canvas.getContext('2d')
      if (!context) {
        return false
      }

      const { width, height } = canvas
      if (!width || !height) {
        return false
      }

      const pixels = context.getImageData(0, 0, width, height).data
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] !== 0) {
          return true
        }
      }
      return false
    },

    save(options = {}) {
      const { silent = false, clearOnEmpty = true } = options
      const data = this.buildCompositeImage()

      if (!data) {
        if (clearOnEmpty) {
          this.$store.dispatch({ type: 'setImg', value: '' })
        }
        if (!silent) {
          return alert('Please provide a signature first.')
        }
        return false
      }

      this.$store.dispatch({ type: 'setImg', value: data })
      this.appliedDrawSource = data
      return true
    },

    applyDummyWriting(payload = {}) {
      const captureAfter = payload.captureAfter === true
      if (!this.allPagesReady()) {
        this.pendingDummy = true
        this.pendingDummyPayload = payload
        this.pendingDummyCapture = captureAfter
        return
      }

      const title = payload.title || 'Demo Contract'
      const seedOffset = payload.seedOffset || 0
      const jitter = () => (Math.random() - 0.5) * (8 + seedOffset)

      const firstCanvas = this.getPageCanvas(0)
      const lastCanvas = this.getPageCanvas(this.templatePages.length - 1)

      if (firstCanvas) {
        const context = firstCanvas.getContext('2d')
        if (context) {
          const fontSize = Math.max(Math.round(firstCanvas.width / 25), 24)
          const titleX = Math.round(firstCanvas.width * 0.05 + jitter())
          const titleY = Math.round(firstCanvas.height * 0.1 + jitter())

          context.fillStyle = '#000'
          context.strokeStyle = '#000'
          context.lineWidth = Math.max(Math.round(firstCanvas.width / 600), 2)
          context.font = `${fontSize}px sans-serif`
          context.fillText(title, titleX, titleY)

          context.beginPath()
          context.moveTo(titleX, titleY + fontSize * 0.6 + jitter())
          context.bezierCurveTo(
            titleX + firstCanvas.width * 0.2 + jitter(),
            titleY + fontSize * 1.2 + jitter(),
            titleX + firstCanvas.width * 0.4 + jitter(),
            titleY + fontSize * 0.2 + jitter(),
            titleX + firstCanvas.width * 0.6 + jitter(),
            titleY + fontSize * 0.8 + jitter()
          )
          context.stroke()
        }
      }

      if (lastCanvas) {
        const context = lastCanvas.getContext('2d')
        if (context) {
          const sigY = Math.round(lastCanvas.height * 0.78 + jitter())

          context.strokeStyle = '#000'
          context.lineWidth = Math.max(Math.round(lastCanvas.width / 500), 2)
          context.beginPath()
          context.moveTo(lastCanvas.width * 0.52 + jitter(), sigY + jitter())
          context.bezierCurveTo(
            lastCanvas.width * 0.6 + jitter(),
            sigY - 24 + jitter(),
            lastCanvas.width * 0.7 + jitter(),
            sigY + 18 + jitter(),
            lastCanvas.width * 0.83 + jitter(),
            sigY - 10 + jitter()
          )
          context.stroke()
        }
      }

      this.activePageIndex = this.templatePages.length - 1

      if (captureAfter) {
        this.save({ silent: true, clearOnEmpty: false })
      }
    },

    flushPendingDummy() {
      if (!this.pendingDummy || !this.allPagesReady()) {
        return
      }

      this.pendingDummy = false
      const payload = this.pendingDummyPayload || {}
      const shouldCapture = this.pendingDummyCapture
      this.pendingDummyPayload = null
      this.pendingDummyCapture = false
      this.applyDummyWriting(payload)
      if (shouldCapture) {
        this.save({ silent: true, clearOnEmpty: false })
      }
    },

    clearPad() {
      this.pagePads.forEach(signaturePad => {
        if (signaturePad) {
          signaturePad.clear()
        }
      })

      this.getPageCanvases().forEach(canvas => {
        const context = canvas.getContext('2d')
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height)
        }
      })

      this.appliedDrawSource = ''
      this.$store.dispatch({ type: 'setImg', value: '' })
    },

    emitInterface() {
      this.$emit('interface', {
        save: () => this.save(),
        capture: () => this.save({ silent: true, clearOnEmpty: true }),
        applyDummyWriting: payload => this.applyDummyWriting(payload),
        clear: () => this.clearPad(),
        setTool: tool => this.setTool(tool)
      })
    },

    handleResize() {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer)
      }

      this.resizeTimer = window.setTimeout(() => {
        const snapshot = this.buildCompositeImage()

        this.templatePages.forEach((page, index) => {
          this.setupPagePad(index)
        })

        if (snapshot) {
          this.loadCompositeImage(snapshot)
        } else {
          this.maybeApplyExistingDrawing(this.draw)
        }

        this.flushPendingDummy()
      }, 120)
    }
  }
}
</script>

<style scoped>
.draw-editor {
  padding-left: 0;
  padding-right: 0;
}

.page-stack {
  max-width: 920px;
  margin: 0 auto;
}

.page-layer {
  position: relative;
  width: 100%;
}

.page-image,
.appendix-page {
  display: block;
  width: 100%;
}

.appendix-page {
  max-width: 920px;
  margin: 12px auto 0;
}

.page-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  cursor: crosshair;
}
</style>
